import { Locale, LOCALES } from '@longy/common'
import { toCanonicalPath } from '@longy/common/cms'

import { buildOgMeta } from './og'

const SITE_NAME = 'Long-Y'

type WebsiteSettings = {
    ogTitle?: string | null
    ogDescription?: string | null
    ogImageId?: string | null
} | null

export type CmsHeadPage = {
    title: string
    locale: Locale
    lead: string | null
    seoTitle: string | null
    seoDescription: string | null
    noIndex: boolean
    canonicalPath: string
    localizedSlugs: Partial<Record<Locale, string>>
    coverMedia: { url: string; width?: number | null; height?: number | null } | null
}

/**
 * The origin is an argument, not something read from the environment here.
 *
 * These builders are otherwise pure — same page in, same head out — and reaching for
 * `import.meta.env` would put the one input the tests cannot set out of their reach. The route
 * supplies it; `readSiteOrigin` is the single place that reads it.
 */
export const readSiteOrigin = (): string =>
    (import.meta.env['VITE_WEB_URL'] || '').replace(/\/$/, '')

export const absoluteUrl = (origin: string, path: string): string =>
    `${origin.replace(/\/$/, '')}${toCanonicalPath(path)}`

/**
 * An explicit seo title is used verbatim: editors author the complete string, and appending the
 * site name would risk duplicating a brand they already included and overflowing the ~60 characters
 * a result actually shows.
 */
export const buildCmsTitle = (page: Pick<CmsHeadPage, 'title' | 'seoTitle'>): string =>
    page.seoTitle || `${page.title} — ${SITE_NAME}`

/**
 * One `<url>` worth of alternates per locale the page exists in, plus `x-default` pointing at the
 * default locale.
 *
 * With per-locale slugs each language is a separate url, so these are what tells a crawler the two
 * are translations of one another rather than duplicates.
 */
export const buildHreflangLinks = (
    origin: string,
    localizedSlugs: Partial<Record<Locale, string>>,
) => {
    const available = LOCALES.filter((locale) => localizedSlugs[locale] !== undefined)
    if (available.length < 2) return []

    const links: Array<{ rel: string; hrefLang: string; href: string }> = available.map(
        (locale) => ({
            rel: 'alternate',
            hrefLang: locale,
            href: absoluteUrl(origin, `/${localizedSlugs[locale] ?? ''}`),
        }),
    )

    const defaultSlug = localizedSlugs.hu
    if (defaultSlug !== undefined) {
        links.push({
            rel: 'alternate',
            hrefLang: 'x-default',
            href: absoluteUrl(origin, `/${defaultSlug}`),
        })
    }

    return links
}

export const buildCmsPageHead = (
    page: CmsHeadPage,
    websiteSettings: WebsiteSettings,
    siteOrigin: string,
) => {
    const title = buildCmsTitle(page)
    const canonical = absoluteUrl(siteOrigin, page.canonicalPath)

    const meta = [
        ...buildOgMeta({
            title,
            // An empty string would defeat the fallback, which is why the api stores null for a
            // blank seo field rather than ''.
            description: page.seoDescription || page.lead,
            url: canonical,
            websiteSettings,
        }),
        ...(page.noIndex ? [{ name: 'robots', content: 'noindex' }] : []),
        ...(page.coverMedia?.url
            ? [
                  { property: 'og:image', content: page.coverMedia.url },
                  ...(page.coverMedia.width
                      ? [{ property: 'og:image:width', content: String(page.coverMedia.width) }]
                      : []),
                  ...(page.coverMedia.height
                      ? [{ property: 'og:image:height', content: String(page.coverMedia.height) }]
                      : []),
              ]
            : []),
    ]

    return {
        meta,
        links: [
            { rel: 'canonical', href: canonical },
            ...buildHreflangLinks(siteOrigin, page.localizedSlugs),
        ],
    }
}

/**
 * The head for a missed load.
 *
 * The loader threw, so `head()` has no data to work from — but the 404 or 410 shell still renders,
 * and without this a crawler would happily index it.
 */
export const buildCmsMissHead = (title: string) => ({
    meta: [{ title: `${title} — ${SITE_NAME}` }, { name: 'robots', content: 'noindex' }],
    links: [],
})
